import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';

// ids scopes the export to one or a few rows (the per-row "Export" action); omitted, the whole
// domain's instances travel.
const BundleExportModal = ({ opened, onClose, apiBase, ids }) => {
  const fetch = useFetch();

  const [json, setJson] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  // The caller allocates ids inline, so the array itself is a new value on every render of the
  // list; the query it produces is what this read actually depends on.
  const query = ids?.length ? `?ids=${ids.join(',')}` : '';

  useEffect(() => {
    if (!opened) return;
    setJson('');
    setError(null);
    setCopied(false);

    fetch(`${apiBase}/bundle${query}`)
      .then((res) => setJson(JSON.stringify(res.bundle, null, 2)))
      .catch((err) => setError(getErrorMessage(err, 'The bundle could not be exported.')));
  }, [opened, apiBase, query, fetch]);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const copy = () => {
    // jsdom and some older browsers have no Clipboard API; the button just does nothing there.
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Export bundle" size="lg" centered>
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          A bundle carries no secret: each secret appears as a slot that is mapped again on import.
        </Text>

        {error && <ErrorMessage message={error} />}

        <Textarea
          readOnly
          autosize
          minRows={10}
          maxRows={20}
          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          value={json}
        />

        <Group justify="flex-end">
          <Button variant="default" disabled={!json} onClick={copy}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default BundleExportModal;
